import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { Icon, LoadingPulse } from "../../ui";
import { displaySlotValue, historyRowsForSlot } from "../../lib/softoneSlotSync";
import { formatScheduleChangeTime, scheduleChangeRowDescription } from "./scheduleComponents";

function formatHistoryStamp(changedAt, lang) {
  if (!changedAt) return "—";
  try {
    const d = new Date(changedAt);
    if (Number.isNaN(d.getTime())) return "—";
    const date = d.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", {
      day: "2-digit",
      month: "short",
    });
    return `${date} ${formatScheduleChangeTime(changedAt)}`;
  } catch {
    return "—";
  }
}

function statusBadge(row, lang) {
  const source = String(row.source || "local").toLowerCase();
  const hr = String(row.hr_status || "").toLowerCase();
  if (source === "local" && (hr === "pending" || !hr)) {
    return { label: lang === "gr" ? "Πρόχειρο" : "Draft", bg: "#FEF3C7", color: "#92400E" };
  }
  if (hr === "sent") {
    return { label: lang === "gr" ? "Στάλθηκε" : "Sent", bg: "#DBEAFE", color: "#1D4ED8" };
  }
  if (hr === "confirmed") {
    return { label: lang === "gr" ? "Επιβεβαιώθηκε" : "Confirmed", bg: "#DCFCE7", color: "#15803D" };
  }
  if (source === "foxline") {
    return { label: "SoftOne", bg: "#E0E7FF", color: "#3730A3" };
  }
  if (source === "upload") {
    return { label: lang === "gr" ? "Αποστολή" : "Upload", bg: "#E0E7FF", color: "#3730A3" };
  }
  return { label: source, bg: "var(--surface2)", color: "var(--text-muted)" };
}

export function SlotHistoryModal({
  open,
  onClose,
  lang,
  unifiedLocationId = null,
  weekStartISO,
  employeeCode,
  employeeId,
  employeeName,
  dateISO,
  currentValue,
  extraRows = [],
}) {
  const [remoteRows, setRemoteRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !weekStartISO || unifiedLocationId == null) return undefined;
    let cancelled = false;
    setLoading(true);
    const url = `${API_BASE}/schedule/changes?unified_location_id=${encodeURIComponent(unifiedLocationId)}&week_start=${encodeURIComponent(weekStartISO)}`;
    fetch(url, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled) setRemoteRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRemoteRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, weekStartISO, unifiedLocationId]);

  const rows = useMemo(
    () =>
      historyRowsForSlot([...(extraRows || []), ...remoteRows], employeeCode, dateISO, {
        employeeId,
        employeeName,
      }),
    [extraRows, remoteRows, employeeCode, dateISO, employeeId, employeeName]
  );

  if (!open) return null;

  const t = (en, gr) => (lang === "gr" ? gr : en);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="slot-history-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 13000,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          borderRadius: 14,
          border: "1px solid var(--border)",
          maxWidth: 440,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 18,
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div>
            <div id="slot-history-title" style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>
              {t("Slot history", "Ιστορικό βάρδιας")}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginTop: 4 }}>
              {employeeName || employeeCode || "—"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{dateISO || "—"}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Close", "Κλείσιμο")}
            style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-secondary)" }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            borderRadius: 10,
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>
            {t("Current (incl. draft)", "Τρέχουσα (και πρόχειρο)")}
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-primary)" }}>
            {displaySlotValue(currentValue, lang)}
          </div>
        </div>

        <div style={{ marginTop: 16, fontSize: 12, fontWeight: 800, color: "var(--text-primary)" }}>
          {t("All logged changes", "Όλες οι καταγεγραμμένες αλλαγές")}
        </div>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-muted)", fontSize: 13, marginTop: 10 }}>
            <LoadingPulse size={18} />
            {t("Loading…", "Φόρτωση…")}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
            {t("No logged events for this slot yet.", "Δεν υπάρχουν καταγεγραμμένα γεγονότα για αυτό το slot.")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {rows.map((row, i) => {
              const badge = statusBadge(row, lang);
              return (
                <div
                  key={row.id || `${row.changed_at || ""}-${i}`}
                  style={{ background: "var(--surface2)", borderRadius: 8, padding: "8px 10px", border: "1px solid var(--border)" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                    <div style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 700 }}>
                      {scheduleChangeRowDescription(row, lang)}
                    </div>
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "2px 6px",
                        borderRadius: 999,
                        background: badge.bg,
                        color: badge.color,
                      }}
                    >
                      {badge.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                    {formatHistoryStamp(row.changed_at, lang)}
                    {row.changed_by_name ? (lang === "gr" ? ` από ${row.changed_by_name}` : ` by ${row.changed_by_name}`) : ""}
                    {row.source ? ` · ${row.source}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
